"use client";

import { useState, useTransition } from "react";

type Branding = "jana" | "stock";

const COOKIE = "jana-ui-branding";

/**
 * DEV-ONLY floating switch between Jana's brand tokens and pure stock shadcn
 * (`[data-branding="stock"]` in globals.css) — the known-good baseline the
 * migration was inspected against. The root layout renders this only under
 * NODE_ENV=development, and getUiBranding() ignores the cookie outside it,
 * so nothing here can surface in production.
 *
 * Same shape as <ThemeToggle>: flip the attribute synchronously for an
 * instant repaint, persist the choice in a cookie so the server agrees on
 * the next render, and nudge a transition so SSR'd subtrees re-read it.
 * Deliberately NOT on the shadcn Button: this widget must look identical in
 * both modes — it is the instrument, not the subject.
 */
export function BrandingDevToggle({ initialBranding }: { initialBranding: Branding }) {
	const [branding, setBranding] = useState<Branding>(initialBranding);
	const [, startTransition] = useTransition();

	function flip() {
		const next: Branding = branding === "jana" ? "stock" : "jana";
		setBranding(next);
		if (next === "stock") document.documentElement.setAttribute("data-branding", "stock");
		else document.documentElement.removeAttribute("data-branding");
		document.cookie = `${COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
		startTransition(() => {});
	}

	return (
		<button
			type="button"
			onClick={flip}
			title={
				branding === "jana"
					? "Dev: switch to stock shadcn tokens"
					: "Dev: switch to Jana brand tokens"
			}
			style={{
				position: "fixed",
				insetInlineStart: "0.5rem",
				bottom: "0.5rem",
				zIndex: 100,
				font: "600 10px/1 ui-monospace, monospace",
				padding: "4px 8px",
				borderRadius: "999px",
				border: "1px solid #666",
				background: branding === "stock" ? "#666" : "#be185d",
				color: "#fff",
				opacity: 0.6,
				cursor: "pointer",
			}}
		>
			{branding === "stock" ? "stock shadcn" : "jana brand"}
		</button>
	);
}
